import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import UserService from './../../../../shared/services/User.service'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import moment from 'moment'
import { UIInput, UIDatePickerInput, UIDropdown } from './../../../../components/forms/form-fields'
import ReferralPartnerInput from './../ReferralPartnerInput/ReferralPartnerInput.component'
import AvDate from './../AvDate/AvDate.component'
import ReferralAdmin from '../ReferralAdmin/ReferralAdmin.component'
import SalesTrackerStore from './../../store'
import MaxAvDateStore from './../../store/max-av-date.store'
import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import { MDBCollapse, MDBCol } from 'mdb-react-ui-kit'
import appConstants from './../../../../constants/appConstants'
import { toast } from 'react-toastify'
import preventInputUnicodeCharacters from '../../../../shared/utilities/preventInputUnicodeCharacters.function'
import ClientGroupManager from './../ClientGroupManager/ClientGroupManager.component'

import './ClientForm.component.scss'

const StatesList = appConstants.States.map(t => ({
  text: t.name,
  value: t.id,
}))

const toDate = dateStr => {
  if (dateStr) {
    if (typeof dateStr === 'string') return moment(`${dateStr}`.split('T').shift()).toDate()
    if (typeof dateStr === 'object') return dateStr
  }
}

export default observer(({ Contact, AOR }) => {
  const { Policy, setCrmContact, reset } = SalesTrackerStore,
    { isAgeRestricted } = MaxAvDateStore,
    contactId = (Contact && Contact?.id()) || null,
    policyAvEffDate = SalesTrackerStore.Policy?.get('av_eff_date'),
    [avEffDate, setAvEffDate] = useState(null),
    [refPayment, setRefPayment] = useState({
      amt_balance: null,
      amt_paid: null,
      amt_total: null,
    })
  const getLeadSource = lId => {
    lId = lId ? lId : SalesTrackerStore.Policy.get('leadsource_id')
    return lId
      ? SalesTrackerStore.LeadSources.filter(LeadSource => parseInt(LeadSource.id()) === parseInt(lId)).shift()
      : undefined
  }
  const getSellingAgent = async aId => {
    let A = Array.isArray(AOR.sellingAgentList)
      ? AOR.sellingAgentList.filter(u => parseInt(u.value) === parseInt(aId)).shift()
      : null
    if (A) return A

    let agentDetails = await UserService.getUserDetails(aId)
    if (!agentDetails) return

    return [
      {
        text: [agentDetails.u_fname, agentDetails.u_lname].join(' ').trim(),
        value: aId,
      },
    ]
  }

  const getAvSellingAgent = async aId => {
    try {
      return (await getSellingAgent(aId)).shift().text
    } catch (ex) {
      return undefined
    }
  }

  const setSellingAgentId = async value => {
    if (!value) {
      SalesTrackerStore.AOR.sellingAgentId = null
      SalesTrackerStore.AOR.avSellingAgent = ''
      return
    }

    SalesTrackerStore.AOR.sellingAgentId = value
    SalesTrackerStore.AOR.avSellingAgent = await getAvSellingAgent(value)
  }

  const setReferralPartner = (id, name) => {
    if (id && parseInt(id) !== parseInt(SalesTrackerStore.Policy.get('r_partner_id')))
      SalesTrackerStore.Policy.set('r_partner_id', parseInt(id))
    else if (!id && SalesTrackerStore.Policy.get('r_partner_id')) SalesTrackerStore.Policy.set('r_partner_id', null)
  }

  const leadsource_id = SalesTrackerStore.Policy.get('leadsource_id'),
    leadSourcesList = SalesTrackerStore.LeadSources.map(LeadSource => ({
      text: LeadSource.get('ls_name'),
      value: `${LeadSource.id()}`,
      checked: !!(leadsource_id && parseInt(leadsource_id) === parseInt(LeadSource.id())),
    }))

  const showToastForIllegalCharacters = field => {
    toast.warn(
      `You tried to paste invalid text in ${field}, but we’ve attempted to correct it. Please review for accuracy.`,
      { position: toast.POSITION.TOP_RIGHT, autoClose: 7000 }
    )
  }

  const isDisabled = () => {
    if (
      isAgeRestricted &&
      !UserProfileService.isA(['internal-admin', 'internal-staff', 'agency-owner', 'system-admin'], true)
    )
      return true

    if (!SalesTrackerStore.clientType) return true

    if (SalesTrackerStore.clientType === 'group' && !SalesTrackerStore.Policy.get('client_group_id')) return true

    return false
  }

  useEffect(() => {
    setCrmContact(contactId)
    if (!contactId) reset()
  }, [contactId])

  useEffect(() => {
    if (avEffDate === null && policyAvEffDate) setAvEffDate(toDate(policyAvEffDate))
    else if (avEffDate && !policyAvEffDate) setAvEffDate(null)
    else {
      const a =
          avEffDate && typeof avEffDate === 'object'
            ? moment(avEffDate).format('YYYY-MM-DD')
            : (avEffDate ? `${avEffDate}` : '').split('T').shift(),
        p =
          policyAvEffDate && typeof policyAvEffDate === 'object'
            ? moment(policyAvEffDate).format('YYYY-MM-DD')
            : (policyAvEffDate ? `${policyAvEffDate}` : '').split('T').shift()
      if (a && p && /\d{4}-\d{2}-\d{2}/.test(a) && /\d{4}-\d{2}-\d{2}/.test(p) && a !== p)
        setAvEffDate(toDate(policyAvEffDate))
    }
  }, [policyAvEffDate, avEffDate])

  return (
    <div id='ClientForm'>
      <MDBCollapse open={!!SalesTrackerStore.isFetching}>
        <LoadingSpinner size='md' isActive={true} />
      </MDBCollapse>
      <MDBCollapse open={!SalesTrackerStore.isFetching}>
        <div name='avClientForm' className='mb-5' autoComplete='off'>
          <div className='form-row'>
            <MDBCol size='12' className='client-form-header'>
              <div>
                <h6 className='font---lato'>Client Details</h6>
              </div>
              <div>
                <UIDropdown
                  optionHeight={37}
                  disablePortal
                  searchLabel='Search'
                  label='Client Type'
                  name='client_type'
                  options={[
                    {
                      text: 'Individuals & Families',
                      value: 'individual',
                      checked: SalesTrackerStore.clientType === 'individual',
                    },
                    {
                      text: 'Organizations & Groups',
                      value: 'group',
                      checked: SalesTrackerStore.clientType === 'group',
                    },
                  ]}
                  onChange={evt => {
                    SalesTrackerStore.setClientType(evt.target.value)
                  }}
                  value={SalesTrackerStore.clientType}
                />
              </div>
            </MDBCol>
          </div>
          <div className='form-row'>
            <MDBCol size='12' className='av-input-wrapper'>
              <MDBCollapse open={SalesTrackerStore.clientType === 'group'}>
                <ClientGroupManager Policy={SalesTrackerStore.Policy} />
              </MDBCollapse>
            </MDBCol>
          </div>
          <div className='form-row'>
            <MDBCol size='6' lg='4' className='av-input-wrapper'>
              <UIInput
                label='Client First Name'
                name='av_client_fname'
                type='text'
                onChange={evt => {
                  const filteredResult = preventInputUnicodeCharacters(evt.target.value)

                  if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Client First Name')

                  if (filteredResult.fixedString !== SalesTrackerStore.Policy.get('av_client_fname'))
                    SalesTrackerStore.Policy.set('av_client_fname', filteredResult.fixedString)
                }}
                disabled={isDisabled()}
                autoComplete='off'
                required={true}
                value={SalesTrackerStore.Policy.get('av_client_fname')}
                rules={{ required: true, minLength: 2, maxLength: 255 }}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'av_client_fname', validity.isValid)}
              />
            </MDBCol>
            <MDBCol size='6' lg='4' className='av-input-wrapper'>
              <UIInput
                label='Client Last Name'
                name='av_client_lname'
                type='text'
                onChange={evt => {
                  const filteredResult = preventInputUnicodeCharacters(evt.target.value)

                  if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Client Last Name')

                  if (filteredResult.fixedString !== SalesTrackerStore.Policy.get('av_client_lname'))
                    SalesTrackerStore.Policy.set('av_client_lname', filteredResult.fixedString)
                }}
                disabled={isDisabled()}
                autoComplete='off'
                required={true}
                value={SalesTrackerStore.Policy.get('av_client_lname')}
                rules={{ required: true, minLength: 2, maxLength: 255 }}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'av_client_lname', validity.isValid)}
              />
            </MDBCol>
            <MDBCol size='12' lg='4' className='av-input-wrapper'>
              <UIInput
                label='Client Email'
                name='av_client_email'
                type='text'
                onChange={evt => {
                  const filteredResult = preventInputUnicodeCharacters(evt.target.value)
                  if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Client Email')
                  if (filteredResult.fixedString !== SalesTrackerStore.Policy.get('av_client_email'))
                    SalesTrackerStore.Policy.set('av_client_email', filteredResult.fixedString)
                }}
                disabled={isDisabled()}
                autoComplete='off'
                value={SalesTrackerStore.Policy.get('av_client_email')}
                rules={{ minLength: 2, maxLength: 255 }}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'av_client_email', validity.isValid)}
              />
            </MDBCol>
          </div>
          <div className='form-row'>
            <MDBCol size='12' lg='6' className='av-input-wrapper'>
              <UIInput
                label='Client Phone'
                name='av_client_phone'
                type='text'
                onChange={evt => {
                  const filteredResult = preventInputUnicodeCharacters(evt.target.value)
                  if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Client Phone')
                  if (filteredResult.fixedString !== SalesTrackerStore.Policy.get('av_client_phone'))
                    SalesTrackerStore.Policy.set('av_client_phone', filteredResult.fixedString)
                }}
                disabled={isDisabled()}
                autoComplete='off'
                value={SalesTrackerStore.Policy.get('av_client_phone')}
                rules={{ minLength: 2, maxLength: 255 }}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'av_client_phone', validity.isValid)}
              />
            </MDBCol>
            <MDBCol size='12' lg='6' className='av-input-wrapper'>
              <UIDropdown
                optionHeight={37}
                disablePortal
                searchLabel='Search'
                label='Client State'
                name='av_state'
                options={StatesList.map(s => ({
                  ...s,
                  checked:
                    `${SalesTrackerStore.Policy.get('av_state')}`.trim().toLowerCase() ===
                    `${s.value}`.trim().toLowerCase(),
                }))}
                onValueChange={selectedOption => {
                  if (parseInt(selectedOption.value) !== parseInt(SalesTrackerStore.Policy.get('av_state')))
                    SalesTrackerStore.Policy.set('av_state', selectedOption.value)
                }}
                disabled={isDisabled()}
                required={true}
                value={SalesTrackerStore.Policy.get('av_state')}
                rules={{ required: true }}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'av_state', validity.isValid)}
                search
              />
            </MDBCol>
          </div>
          <div className='form-row'>
            <MDBCol size='12' className='submission-form-header'>
              <br />
              <h6 className='font---lato'>Submission Details</h6>
            </MDBCol>
          </div>
          <div className='form-row'>
            <MDBCol size='12' lg='6' className='av-input-wrapper'>
              <AvDate disabled={isDisabled()} />
            </MDBCol>
            <MDBCol size='12' lg='6' className='av-input-wrapper'>
              <UIDropdown
                optionHeight={37}
                disablePortal
                searchLabel='Search'
                label='Lead Source'
                name='leadsource_id'
                options={leadSourcesList}
                onValueChange={selectedOption => {
                  let value = selectedOption.value
                  if (value && value !== parseInt(SalesTrackerStore.Policy.get('leadsource_id'))) {
                    SalesTrackerStore.Policy.set('leadsource_id', value)
                    const LeadSource = getLeadSource()
                    if (LeadSource) SalesTrackerStore.Policy.set('av_leadsource', LeadSource.get('ls_name'))
                  }
                }}
                disabled={isDisabled()}
                required={true}
                value={SalesTrackerStore.Policy.get('leadsource_id')}
                rules={{ required: true }}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'leadsource_id', validity.isValid)}
                search
              />
            </MDBCol>
            <MDBCol size='12' lg='6' className='av-input-wrapper'>
              <UIDropdown
                optionHeight={37}
                disablePortal
                searchLabel='Search'
                label='Agent of Record'
                name='selling_agent_id'
                options={(SalesTrackerStore.AOR.sellingAgentList || []).map(s => ({
                  ...s,
                  checked: parseInt(s.value) === parseInt(SalesTrackerStore.AOR.sellingAgentId),
                }))}
                onChange={evt => {
                  let value = evt.target.value
                  if (value && value !== parseInt(SalesTrackerStore.AOR.sellingAgentId)) setSellingAgentId(value)
                }}
                disabled={isDisabled()}
                value={SalesTrackerStore.AOR.selling_agent_id}
                showValidity={SalesTrackerStore.showErrors}
                onValidityChange={validity => SalesTrackerStore.setValidity(0, 'selling_agent_id', validity.isValid)}
                search
              />
            </MDBCol>
            <MDBCol size='12' lg='6'>
              <ReferralPartnerInput
                agent_id={SalesTrackerStore.AOR.sellingAgentId}
                referral_partner_id={SalesTrackerStore.Policy.get('r_partner_id')}
                set_referral_partner={setReferralPartner}
                disabled={isDisabled() || refPayment.amt_paid > 0 || refPayment.amt_total > 0}
              />
              {SalesTrackerStore.Policy.get('r_partner_id') ? (
                <table
                  width='100%'
                  style={{
                    border: '1px solid #eaeaea',
                    borderRadius: '0.3rem',
                    marginBottom: '0.5rem',
                  }}
                  cellPadding='2'
                >
                  <tbody>
                    <tr>
                      <td valign='middle' className='text--left fs--1rem fw--500'>
                        Referral Payment:
                      </td>
                      <td valign='middle'>
                        <table>
                          <tbody>
                            <tr>
                              <td className='text--right fs--075rem fw--500'>Total:</td>
                              <td>
                                {refPayment.amt_total !== null &&
                                !isNaN(refPayment.amt_total) &&
                                refPayment.amt_total >= 0
                                  ? `$${parseFloat(refPayment.amt_total).toFixed(2)}`
                                  : '--'}
                              </td>
                            </tr>
                            <tr>
                              <td className='text--right fs--075rem fw--500'>Paid:</td>
                              <td>
                                {refPayment.amt_paid !== null && !isNaN(refPayment.amt_paid) && refPayment.amt_paid >= 0
                                  ? `$${parseFloat(refPayment.amt_paid).toFixed(2)}`
                                  : '--'}
                              </td>
                            </tr>
                            <tr>
                              <td className='text--right fs--075rem fw--500'>Balance:</td>
                              <td>
                                {refPayment.amt_balance !== null &&
                                !isNaN(refPayment.amt_balance) &&
                                refPayment.amt_balance >= 0
                                  ? `$${parseFloat(refPayment.amt_balance).toFixed(2)}`
                                  : '--'}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <></>
              )}
            </MDBCol>
          </div>
          <div className='form-row'>
            <MDBCol size='12'>
              <ReferralAdmin
                avId={SalesTrackerStore.Policy.id()}
                referralPartnerId={SalesTrackerStore.Policy.get('r_partner_id')}
                onPaymentChange={payment => {
                  if (payment && typeof payment === 'object') {
                    if (JSON.stringify(refPayment) !== JSON.stringify(payment)) setRefPayment(payment)
                  }
                }}
              />
            </MDBCol>
          </div>
        </div>
      </MDBCollapse>
    </div>
  )
})
